import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Radio, Alert } from 'antd';
import { CloseIcon } from '../../Assets/Svgs';
import { DoctorRes } from '../../types';
import { DoctorPointsManagement } from '../../Actions/ConfigActions';
import commaNumber from 'comma-number';

interface Props {
  open: boolean;
  updateModalOpen: (value: boolean) => void;
  doctor: DoctorRes | undefined;
  accumulationFactor: number;
  setAccumulationFactor: (value: number) => void;
  redemptionRate: number | undefined;
  setRedemptionRate: (value: number | undefined) => void;
  refetch: () => Promise<void> | any;
}

const DoctorPointsManagementModal: React.FC<Props> = ({
  open,
  updateModalOpen,
  doctor,
  accumulationFactor,
  setAccumulationFactor,
  redemptionRate,
  setRedemptionRate,
  refetch,
}) => {
  const [bonusPoints, setBonusPoints] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [points, setPoints] = useState<number>(0);

  const handleOk = async () => {
    if (
      redemptionRate !== undefined &&
      redemptionRate !== null &&
      bonusPoints >= 0 &&
      doctor?.id
    ) {
      try {
        setIsLoading(true);
        await DoctorPointsManagement({
          ids: [doctor.id],
          factor_points: accumulationFactor,
          redemption_rate: redemptionRate,
          add_points: bonusPoints * 10,
        });
        await refetch();
        setIsLoading(false);
        setBonusPoints(0);
        setPoints(points + bonusPoints * redemptionRate);
        updateModalOpen(false);
      } catch (err) {
        setIsLoading(false);
        setErrorMessage('Something went wrong kindly try again');
      }
    }
  };
  const handleCancel = () => {
    updateModalOpen(false);
  };
  useEffect(() => {
    if (doctor?.card_details?.PointsEarned)
      setPoints(+doctor?.card_details?.PointsEarned);
  }, [doctor?.card_details?.PointsEarned]);

  const isSaveDisabled =
    redemptionRate === undefined || redemptionRate === null || bonusPoints <= 0;

  // Determine the bundle type based on the BundleID
  const bundleType =
    doctor?.systemConfigs.BundleID === 'idh.firstclass.loynova.loyalty'
      ? 'First Class'
      : 'Excellence';

  const primaryClass =
    bundleType === 'Excellence' ? 'text-elborgPrimary' : 'text-primary';

  const bgClass =
    bundleType === 'Excellence' ? 'bg-elborgPrimary' : 'bg-primary';

  const buttonClass =
    bundleType === 'Excellence' ? 'bg-elborgPrimary' : 'bg-primary';

  const radioClass =
    bundleType === 'Excellence' ? 'excellence-radio' : 'firstclass-radio';

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      centered
      width={600}
      closeIcon={
        <button onClick={handleCancel}>
          <CloseIcon />
        </button>
      }
      footer={null}
    >
      <div className={'flex justify-between mb-6 text-xl font-semibold'}>
        <span>
          Dr. {doctor?.name} ({doctor?.id}) - Points Management
        </span>
      </div>
      <div
        className={`flex justify-between mb-2 text-sm font-semibold ${primaryClass}`}
      >
        <span className="flex-1 text-left">{bundleType}</span>
        <span className="flex-1 mr-1 text-center">Redemption Rate</span>
        <span className="flex-1 mr-8 text-right">Add Bonus Points</span>
      </div>
      <div className="flex justify-between mb-6 space-x-8">
        <div className="flex-1 text-left">
          <span
            className={`block p-2 text-lg font-semibold text-center text-white rounded-lg ${bgClass}`}
          >
            {`${points ? commaNumber(+points / 10) : 0}`.split('.')[0] ?? 0}
            <sub className="ml-1 text-xs">PTS</sub>
            <span className="block text-sm font-semibold text-center">
              Current Points
            </span>
            <p className="text-xs">
              Equivalent To{' '}
              {`${points ? commaNumber(+points / 100) : 0}`.split('.')[0] ?? 0}
              &nbsp; EGP
            </p>
          </span>
        </div>
        <div className="flex-1 text-left">
          <div className="relative">
            <Input
              type="number"
              value={redemptionRate}
              disabled={true}
              onChange={(e) =>
                setRedemptionRate(
                  e.target.value ? parseFloat(e.target.value) : undefined
                )
              }
              className="block px-2 py-3 rounded-lg"
              style={{ textAlign: 'left' }}
            />
            <span className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2">
              %
            </span>
          </div>
        </div>
        <div className="flex-1 text-left">
          <Input
            type="number"
            value={bonusPoints}
            onChange={(e) =>
              setBonusPoints(e.target.value ? parseFloat(e.target.value) : 0)
            }
            className="block px-2 py-3 rounded-lg"
          />
        </div>
      </div>
      <div className={`mb-4 text-sm font-semibold ${primaryClass}`}>
        Accumulation Factor
      </div>
      <div
        className={`grid grid-cols-4 gap-4 mb-6 text-sm font-normal ${radioClass}`}
      >
        {[
          { label: '@x0.25', value: 0.25 },
          { label: '@x0.5', value: 0.5 },
          { label: '@x0.75', value: 0.75 },
          { label: '@x1', value: 1 },
          { label: '@x1.25', value: 1.25 },
          { label: '@x1.5', value: 1.5 },
          { label: '@x1.75', value: 1.75 },
          { label: '@x2', value: 2 },
        ].map((factor) => (
          <Radio
            key={factor.value}
            checked={accumulationFactor === factor.value}
            onChange={() => {
              setAccumulationFactor(factor.value);
            }}
          >
            {factor.label}
          </Radio>
        ))}
      </div>
      {errorMessage && <Alert message={errorMessage} type="error" />}
      <Button
        type="primary"
        onClick={handleOk}
        disabled={isLoading}
        loading={isLoading}
        className={`w-full font-semibold text-base text-White ${buttonClass} ${!isSaveDisabled && bundleType === 'Excellence' ? 'hover:!bg-[#f5cd5f]' : ''} ${!isSaveDisabled && bundleType === 'First Class' ? 'hover:!bg-[#CC414B]' : ''}`}
      >
        Save
      </Button>
    </Modal>
  );
};

export default DoctorPointsManagementModal;
